import { Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { Pencil, Trash2, UserX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AccountHolderCell, DateCell } from '@/components/data-table/cells';
import { DataTable } from '@/components/data-table/data-table';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { FeeRunExclusionDialog } from '@/features/fee-runs/fee-run-exclusion-dialog';
import { exclusionReasonLabel, parseExclusionError } from '@/features/fee-runs/fee-run-exclusions';
import { useFeeRunExclusions, useRemoveFeeRunExclusion } from '@/hooks/queries/fees/use-fee-runs';
import type { FeeRunExclusion } from '@/services/api/fee-runs';

const FETCH_ERROR_TITLE = 'Failed to fetch fee exclusions';

interface DialogState {
  open: boolean;
  editing: FeeRunExclusion | null;
}

/** Account holders ops have taken out of every fee run, with why and who did it. */
export const FeeRunExclusionsCard = () => {
  const { data, isPending, isFetching, isError, error, refetch } = useFeeRunExclusions();
  const removeMutation = useRemoveFeeRunExclusion();
  const [dialog, setDialog] = useState<DialogState>({ open: false, editing: null });
  const reasons = data?.reasons;
  const canManage = data?.capabilities.canManage ?? false;

  const columns = useMemo((): ColumnDef<FeeRunExclusion, unknown>[] => {
    const handleRemove = async (exclusion: FeeRunExclusion) => {
      const justification = await confirmAction({
        title: 'Remove this exclusion?',
        description: 'The account holder is charged by fee runs calculated from now on.',
        actionLabel: 'Remove exclusion',
        destructive: true,
        onConfirm: async (comment) => {
          try {
            await removeMutation.mutateAsync({ pid: exclusion.pid, comment: comment.trim() });
          } catch (removeError) {
            throw new Error(parseExclusionError(removeError).message);
          }
        },
      });
      if (justification !== null) toast.success('Exclusion removed');
    };

    return [
      { accessorKey: 'pid', header: 'Account holder', cell: ({ row }) => <AccountHolderCell value={row.original.pid} /> },
      { accessorKey: 'reason', header: 'Reason', cell: ({ row }) => <span className="whitespace-nowrap">{exclusionReasonLabel(reasons, row.original.reason)}</span> },
      { accessorKey: 'comment', header: 'Comment', cell: ({ row }) => <span className="line-clamp-2 max-w-md text-sm text-muted-foreground">{row.original.comment}</span> },
      { accessorKey: 'addedBy', header: 'Added by', cell: ({ row }) => <span className="text-xs">{row.original.addedBy}</span> },
      { accessorKey: 'addedAt', header: 'Added', cell: ({ row }) => <DateCell value={row.original.addedAt} /> },
      ...(canManage
        ? [
            {
              id: 'actions',
              header: '',
              cell: ({ row }) => (
                <div className="flex justify-end gap-1">
                  <Button aria-label="Change reason" onClick={() => setDialog({ open: true, editing: row.original })} size="icon" type="button" variant="ghost">
                    <Pencil className="size-4" />
                  </Button>
                  <Button aria-label="Remove exclusion" onClick={() => void handleRemove(row.original)} size="icon" type="button" variant="ghost">
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ),
            } satisfies ColumnDef<FeeRunExclusion, unknown>,
          ]
        : []),
    ];
  }, [reasons, canManage, removeMutation]);

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <div className="flex items-center gap-2">
          <CardTitle>Exclusions</CardTitle>
          {isFetching && !isPending ? <Spinner className="size-4" /> : null}
        </div>
        {canManage ? (
          <Button className="gap-2" onClick={() => setDialog({ open: true, editing: null })} type="button" variant="outline">
            <UserX className="size-4" />
            Add exclusion
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">These account holders are never charged custody or platform fees. Each run lists them in its Exclusions report.</p>
        {isError ? <QueryErrorBanner error={error} onRetry={() => refetch()} title={FETCH_ERROR_TITLE} /> : null}
        <DataTable columns={columns} data={data?.exclusions} emptyMessage="No account holders are excluded." isLoading={isPending} surface={false} />
      </CardContent>
      {reasons ? (
        <FeeRunExclusionDialog editing={dialog.editing} onOpenChange={(open) => setDialog((previous) => ({ ...previous, open }))} open={dialog.open} reasons={reasons} />
      ) : null}
    </Card>
  );
};
FeeRunExclusionsCard.displayName = 'FeeRunExclusionsCard';
